import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

const errMsg = (err, fallback) =>
  err.response?.data?.message ||
  err.response?.data?.errors?.[0]?.message ||
  err.message ||
  fallback;

// Returns one row per device in the user's company:
//   [{ device, telemetry|null }, …]
export const fetchMyTelemetry = createAsyncThunk(
  "telemetry/myLatest",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/telemetry/me/latest");
      return data.data.items || [];
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load telemetry"));
    }
  }
);

// History for a single IMEI — chronological list of payloads between
// sinceMs (inclusive) and toMs (inclusive).
export const fetchMyHistory = createAsyncThunk(
  "telemetry/myHistory",
  async ({ imei, sinceMs, toMs, limit = 5000 } = {}, { rejectWithValue }) => {
    try {
      const params = { imei, limit };
      if (sinceMs) params.sinceMs = sinceMs;
      if (toMs) params.toMs = toMs;
      const { data } = await api.get("/telemetry/me/history", { params });
      return data.data.items || [];
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load history"));
    }
  }
);

const slice = createSlice({
  name: "telemetry",
  initialState: {
    items: [],
    loading: false,
    lastFetchedAt: null,
    error: null,
    history: [],
    historyLoading: false,
    historyError: null,
  },
  reducers: {
    clearError(state) { state.error = null; },
  },
  extraReducers: (b) => {
    b.addCase(fetchMyTelemetry.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchMyTelemetry.fulfilled, (s, a) => {
        s.loading = false;
        s.items = a.payload;
        s.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchMyTelemetry.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(fetchMyHistory.pending, (s) => {
        s.historyLoading = true;
        s.historyError = null;
      })
      .addCase(fetchMyHistory.fulfilled, (s, a) => {
        s.historyLoading = false;
        s.history = a.payload;
      })
      .addCase(fetchMyHistory.rejected, (s, a) => {
        s.historyLoading = false;
        s.historyError = a.payload;
      });
  },
});

export const { clearError } = slice.actions;
export default slice.reducer;
